// src/pages/ResetPassword.jsx
// Public route (no session yet). Reached via the link in the
// KaiLux-branded reset email sent by POST /api/auth/request-password-reset
// (see main.py + emails/sender.py's send_password_reset):
//   /reset-password?token_hash=<...>&type=recovery
//
// Two-stage flow:
//   1. On mount, verifyOtp({token_hash, type:'recovery'}) against Supabase
//      directly — this is the same call SsoLogin.jsx makes for its own
//      magiclink flow, just with type='recovery'. Supabase's own OTP
//      handling gives one-time-use + expiry (1hr) for free; no separate
//      backend endpoint is needed to "confirm" the token, unlike
//      request-side which needed the admin API to mint it.
//   2. Once that succeeds we have a real (but purpose-limited) session —
//      show the new-password form, call supabase.auth.updateUser(). Then
//      sign out immediately and send them to /login to sign in fresh with
//      the new password, rather than leaving an active session sitting
//      open on whatever device the email link happened to be opened on.
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../supabase'
import ErrorMessage from '../components/ErrorMessage'


import { T, alpha } from '../theme/theme'
export default function ResetPassword() {
  const [params] = useSearchParams()
  const tokenHash = params.get('token_hash') || ''
  const navigate = useNavigate()

  // verifying | ready | error | saving | done
  const [stage, setStage]       = useState('verifying')
  const [error, setError]       = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')

  useEffect(() => {
    if (!tokenHash) {
      setStage('error')
      setError('This reset link is missing its token. Please request a new one.')
      return
    }
    let cancelled = false
    ;(async () => {
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash, type: 'recovery',
      })
      if (cancelled) return
      if (otpError) {
        setStage('error')
        setError('This reset link has expired or has already been used. Please request a new one.')
      } else {
        setStage('ready')
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenHash])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setStage('saving'); setError('')
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError
      setStage('done')
      await supabase.auth.signOut()
      setTimeout(() => navigate('/login', { replace: true }), 1800)
    } catch (err) {
      setError(err.message || 'Could not update your password. Please try again.')
      setStage('ready')
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex',
      alignItems:'center', justifyContent:'center', padding:'20px',
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <div style={{ width:'100%', maxWidth:'400px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'32px' }}>

        <div style={{ textAlign:'center', marginBottom:'20px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
            fontWeight:'800' }}>
            Kai<span style={{ color:T.or }}>Lux</span>
          </div>
        </div>

        {stage === 'verifying' && (
          <div style={{ fontSize:'14px', color:T.mt, textAlign:'center', padding:'20px 0' }}>
            Verifying your reset link…
          </div>
        )}

        {stage === 'error' && (
          <>
            <ErrorMessage style={{ margin:'14px 0', textAlign:'left' }}>{error}</ErrorMessage>
            <div style={{ textAlign:'center' }}>
              <Link to="/forgot-password" style={{ color:T.or, fontSize:'13px', fontWeight:'700',
                textDecoration:'none' }}>Request a new link</Link>
            </div>
          </>
        )}

        {(stage === 'ready' || stage === 'saving') && (
          <>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px',
              fontWeight:'800', marginBottom:'6px', textAlign:'center' }}>
              Set a new password
            </div>
            <div style={{ fontSize:'13px', color:T.mt, marginBottom:'20px', textAlign:'center' }}>
              Choose a new password for your KaiLux account.
            </div>

            {error && <ErrorMessage style={{ marginBottom:'16px' }}>{error}</ErrorMessage>}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  NEW PASSWORD
                </label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                  placeholder="Min 8 characters" autoComplete="new-password" autoFocus
                  style={{ width:'100%', padding:'12px 14px', background:T.s2,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                    outline:'none', boxSizing:'border-box' }}
                  onFocus={e=>e.target.style.borderColor=T.or}
                  onBlur={e=>e.target.style.borderColor=T.bd}/>
              </div>

              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  CONFIRM NEW PASSWORD
                </label>
                <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
                  placeholder="Retype your new password" autoComplete="new-password"
                  style={{ width:'100%', padding:'12px 14px', background:T.s2,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                    outline:'none', boxSizing:'border-box' }}
                  onFocus={e=>e.target.style.borderColor=T.or}
                  onBlur={e=>e.target.style.borderColor=T.bd}/>
              </div>

              <button type="submit" disabled={stage==='saving'}
                style={{ width:'100%', padding:'12px', borderRadius:'8px', border:'none',
                  background:stage==='saving'?T.mt:T.prg,
                  color:T.onpri, fontFamily:"'Outfit',sans-serif",
                  fontSize:'15px', fontWeight:'700', cursor:stage==='saving'?'not-allowed':'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                {stage === 'saving' ? (
                  <>
                    <span style={{ width:'14px', height:'14px',
                      border:`2px solid ${alpha(T.onpri, 0.3)}`,
                      borderTopColor:T.onpri, borderRadius:'50%',
                      animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                    Saving...
                  </>
                ) : 'Update password →'}
              </button>
            </form>
          </>
        )}

        {stage === 'done' && (
          <div style={{ fontSize:'14px', color:T.mt, textAlign:'center', padding:'10px 0' }}>
            Your password has been updated. Redirecting you to login…
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
